import { animate, MotionValue, useMotionValue } from 'framer-motion';
import {
  useEffect, useState, useCallback, useRef, MutableRefObject,
} from 'react';
import { TChestReward, TChestRewardHistory } from '../../../types';
import { spinnerGenerator } from '../utils/spinnerGenerator';

type TUseSpinAnimationOptions = {
  onFinish?: (reward: TChestRewardHistory) => void | Promise<void>;
};

type TUseSpinAnimationProps = {
  rewards: TChestReward[];
};
export type TUseSpinAnimationResponse = {
  spinLine: TChestReward[],
  scope: MutableRefObject<HTMLDivElement | null>,
  spin: (reward: TChestRewardHistory) => Promise<void>,
  isSpinning: boolean,
  x: MotionValue<number>
}

export const useSpinAnimation = (
  { rewards }: TUseSpinAnimationProps,
  options?: TUseSpinAnimationOptions,
): TUseSpinAnimationResponse => {
  const scope = useRef<HTMLDivElement | null>(null);
  const [spinLine, setSpinLine] = useState<TChestReward[]>([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const x = useMotionValue(0);

  useEffect(() => {
    if (rewards.length) {
      setSpinLine(spinnerGenerator(rewards));
    }
  }, [rewards]);

  const getChildPosition = useCallback(() => {
    if (!scope.current) return { x: 0, width: 0 };

    const child = scope.current.children[1]; // Base index 1
    if (!child) return { x: 0, width: 0 };

    const rect = child.getBoundingClientRect();
    return { x: rect.x, width: rect.width };
  }, []);

  useEffect(() => {
    if (scope.current && spinLine.length) {
      const { x: pos, width } = getChildPosition();
      const centerX = -(pos - window.outerWidth / 2 + width / 2);
      x.set(centerX);
    }
  }, [scope.current, spinLine.length, getChildPosition]);

  const spin = async (reward: TChestRewardHistory) => {
    if (!scope.current) return;

    setIsSpinning(true);
    setSpinLine((prev) => prev.map((r, idx) => (idx % 10 === 1 ? reward : r)));

    const spinLineWidth = scope.current.scrollWidth;
    const section = spinLineWidth / 3;

    const duration = 1;

    const basePos = x.get();

    let startPos = basePos;
    let finishPos = -(section - startPos);

    await animate(x, [startPos, finishPos], {
      ease: 'linear',
      duration,
      repeat: 2,
      repeatDelay: 0,
      repeatType: 'loop',
      type: 'keyframes',
      onComplete: () => {
        x.set(basePos);
      },
    });

    startPos = x.get();
    finishPos = -(section - startPos);

    const ctrls = animate(x, [startPos, finishPos], {
      ease: 'linear',
      duration,
      onComplete: () => {
        x.set(basePos);
      },
    });

    let speed = 1;

    await new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (speed > 0.1) {
          speed -= 0.05;

          ctrls.speed = speed;
        } else {
          clearInterval(intervalId);

          resolve(null);

          ctrls.complete();
        }
      }, 100);
    });

    await new Promise((resolve) => { setTimeout(() => { resolve(null); }, 500); });

    setIsSpinning(false);

    await options?.onFinish?.(reward);
  };

  return {
    scope,
    spin,
    x,
    spinLine,
    isSpinning,
  };
};
